import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { LocationOrdersModel } from '../../models/LocationOrders';
import { Card } from './Card';

// 🎯 Interface Segregation Principle (ISP) - Mismas props que LocationOrdersTable
interface CardListProps {
  locationorders: LocationOrdersModel[];
  isLoading?: boolean;
  className?: string;
  // 🎯 Props para scroll infinito
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting - manejado desde el padre
  sorting?: any;
  onSortingChange?: (sorting: any) => void;
  total?: number;
  // 🎯 Props específicas para cards
  showActions?: boolean;
  // 🎯 Prop para estimación de altura cuando es dinámica
  estimateItemHeight?: number;
  // 🎯 Prop para ocultar columnas seleccionadas
  selectedColumns?: string[];
}

export const CardList = ({
  locationorders,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  className = '',
  showActions = true,
  estimateItemHeight = 120, // 🎯 Estimación conservadora para cards con órdenes de ubicación
  selectedColumns,
}: CardListProps) => {
  const { t } = useTranslation('locationordersModule');

  // 🎯 Render function que usa el componente LocationOrderCard
  const renderLocationOrderCard = (props: RenderItemProps<LocationOrdersModel>) => (
    <Card {...props} showActions={showActions} selectedColumns={selectedColumns} />
  );

  // 🎯 Key generator para optimizar renders
  const getLocationOrderKey = (locationorder: LocationOrdersModel) => {
    return locationorder.id;
  };

  return (
    <VirtualList
      data={locationorders}
      renderItem={renderLocationOrderCard}
      itemKey={getLocationOrderKey}
      // 🎯 Configuración optimizada para móvil con altura dinámica
      estimateItemHeight={estimateItemHeight} // 🎯 Solo estimación, altura real será medida
      height="calc(100vh - 150px)" // Altura completa en móvil
      gap={0} // Sin gap entre cards
      emptyMessage={t('empty.message')}
      className={`rounded-lg shadow-sm ${className}`}
      // 🎯 Props de scroll infinito
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      infiniteScrollThreshold={300} // Threshold mayor para móvil
      // 🎯 Props de sorting (aunque en móvil se use menos)
      sorting={sorting}
      onSortingChange={onSortingChange}
      total={total}
      // 🎯 Styling específico para móvil
      listClassName="touch-pan-y" // Mejor scroll en mobile
      itemClassName="touch-manipulation" // Optimización touch
    />
  );
};
